import React, { createContext, useCallback, useContext, useRef, useState } from "react"

type ToastKind = "info" | "success" | "error"

interface ToastItem {
  id: number
  message: string
  kind: ToastKind
}

interface ToastContextType {
  toast: (message: string, kind?: ToastKind) => void
}

const ToastContext = createContext<ToastContextType>({ toast: () => {} })

const KIND_STYLES: Record<ToastKind, string> = {
  info: "border-slate-200 bg-white text-navy-800",
  success: "border-emerald-200 bg-emerald-50 text-emerald-800",
  error: "border-red-200 bg-red-50 text-red-800",
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const nextId = useRef(1)

  const toast = useCallback((message: string, kind: ToastKind = "info") => {
    const id = nextId.current++
    setItems((prev) => [...prev, { id, message, kind }])
    window.setTimeout(() => {
      setItems((prev) => prev.filter((t) => t.id !== id))
    }, 4000)
  }, [])

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-4 z-[100] flex flex-col items-center gap-2 px-4"
      >
        {items.map((t) => (
          <div
            key={t.id}
            role="status"
            className={`pointer-events-auto w-full max-w-sm rounded-xl border px-4 py-3 text-sm font-medium shadow-lg shadow-slate-900/10 ${KIND_STYLES[t.kind]}`}
          >
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  return useContext(ToastContext)
}
